import React, {
  createContext,
  useMemo,
  useEffect,
  useState,
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { importRemote } from '@module-federation/utilities';
import useDeepCompareEffect from 'use-deep-compare-effect';
import isEmpty from 'lodash/isEmpty';
import useSiteData from '../../hooks/useSiteData';
import { DYNAMIC_TENANT_DETAILS } from '../../constants/moduleFederation';

const ExpressCheckoutContext = createContext(undefined);

const ExpressCheckoutContextProvider = ({ children }) => {
  const {
    tenantEnvironment,
    locale,
    tracking: { brand: trackingTenant },
    externalAppNavigation,
  } = useSiteData();

  const [cartTenantDetails, setCartTenantDetails] = useState();
  const [dynamicExport, setDynamicExport] = useState(undefined);

  const { cart, checkout, orderDetails } = externalAppNavigation;

  useEffect(() => {
    const fetchData = async () => {
      await importRemote({
        url: DYNAMIC_TENANT_DETAILS.url,
        scope: DYNAMIC_TENANT_DETAILS.scope,
        module: DYNAMIC_TENANT_DETAILS.module,
        remoteEntryFileName: DYNAMIC_TENANT_DETAILS.fileName,
        bustRemoteEntryCache: false,
      })
        .then(di => {
          setDynamicExport(di);
        })
        .catch(e => console.error('importRemote Error::', e));
    };

    fetchData().catch(console.error);
  }, []);

  const fetchTenantDetails = useCallback(async () => {
    const appNavOverrides = {
      absoluteCartUrl: cart,
      absoluteCheckoutUrl: checkout,
      absoluteOrderDetailsUrl: orderDetails,
    };

    const retrievedTenantDetails =
      !isEmpty(tenantEnvironment) &&
      (await dynamicExport.getCartTenantDetails(
        trackingTenant,
        locale,
        tenantEnvironment.toLowerCase()
      ));

    if (
      retrievedTenantDetails &&
      Object.keys(retrievedTenantDetails).length > 0
    ) {
      setCartTenantDetails({
        ...retrievedTenantDetails,
        ...appNavOverrides,
      });
    }
  }, [
    cart,
    checkout,
    dynamicExport,
    locale,
    orderDetails,
    tenantEnvironment,
    trackingTenant,
  ]);

  useDeepCompareEffect(() => {
    if (dynamicExport && !cartTenantDetails) {
      fetchTenantDetails().catch(console.error);
    }
  }, [cartTenantDetails, dynamicExport, fetchTenantDetails]);

  const expressCheckoutContextValues = useMemo(
    () => ({
      cartTenantDetails,
    }),
    [cartTenantDetails]
  );

  return (
    <ExpressCheckoutContext.Provider value={expressCheckoutContextValues}>
      {children}
    </ExpressCheckoutContext.Provider>
  );
};

function useExpressCheckoutContext() {
  const context = useContext(ExpressCheckoutContext);
  if (context === undefined) {
    throw new Error(
      'useExpressCheckoutContext must be used within a ExpressCheckoutContextProvider'
    );
  }
  return context;
}

ExpressCheckoutContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export {
  ExpressCheckoutContext,
  ExpressCheckoutContextProvider,
  useExpressCheckoutContext,
};
